import React, { useEffect, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { Formik, FormikValues } from 'formik';
import moment from 'moment';
import * as yup from 'yup';

import Header from 'components/AppBar';
import { AvatarChange } from 'components/AvatarCrop';
import SubmitButton from 'components/Buttons/SubmitButton';
import FormikSubmitErrorEffect from 'components/FormikSubmitErrorEffect';
import Input from 'components/Input';
import Loader from 'components/Loader';
import MonthSelect from 'components/MonthSelect';
import ProfilePhotoInfo from 'components/ProfilePhotoInfo';
import { Desktop, Mobile } from 'components/Responsive';
import ScreenHeader from 'components/ScreenHeader';
import Section from 'components/Section';
import GenreSelector from 'components/Selects/GenreSelector';
import NationalitySelector from 'components/Selects/OnboardingSelect';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { AdminUser } from 'models/adminUser';
import { NationalityCountry } from 'models/country';
import { AccountResponse } from 'models/user';
import { extractErrorMessage, formatISODateToDDMMYYYY, formatDateToBack } from 'utils/generic';

import {
  Container,
  Wrapper,
  FormContainer,
  Line,
  Field,
  CloseButton,
  CloseIcon,
  SectionDateOfBirth,
  Content,
  ErrorValidation,
} from './styles';

interface TravelerUserProfile extends AccountResponse {
  nationality?: string;
}

interface Props {
  isLoading: boolean;
  user: TravelerUserProfile | null;
  userPhoto: string;
  saveUser: (user: AdminUser, companyId: string, id: string) => Promise<void>;
  saveProfileImage: (val: FormData) => Promise<void>;
  fetchCountryList: () => Promise<void>;
  companyId: string;
  id: string;
  nationalityCountries: NationalityCountry[];
  loadingNationalityCountries: boolean;
}

const TravelerFormProfile = ({
  isLoading,
  user,
  saveUser,
  saveProfileImage,
  companyId,
  id,
  userPhoto,
  fetchCountryList,
  nationalityCountries,
  loadingNationalityCountries,
}: Props) => {
  const history = useHistory();
  const { t } = useStoreTranslation();
  const toast = useToast();

  const [newPhoto, setNewPhoto] = useState<AvatarChange>();
  const [loading, setLoading] = useState<boolean>(false);
  const [month, setMonth] = useState<number>();

  useEffect(() => {
    if (!nationalityCountries.length && !loadingNationalityCountries) {
      fetchCountryList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCountryList, nationalityCountries]);

  const stringGroup = user?.id ? 'userForm.update' : 'userForm.create';

  const isNewPhoto = useMemo(() => newPhoto && newPhoto?.image !== userPhoto, [newPhoto, userPhoto]);

  const handleChangePhoto = (val: AvatarChange) => {
    setNewPhoto(val);
  };

  const handleSaveUser = async (values: AdminUser) => {
    if (!user) return;

    const formattedMonth = month && month < 10 ? '0' + month : month;
    const birthDate = `${values.birthDate_day}/${formattedMonth}/${values.birthDate_year}`;

    const data: AdminUser = {
      ...values,
      birthDate: formatDateToBack(birthDate),
      companyId,
      idRappi: user.idRappi,
      type: user.type,
      isTravelArranger: user.isTravelArranger,
      isAllUsersBookAllowed: user.isAllUsersBookAllowed,
      isGuestBookAllowed: user.isGuestBookAllowed,
      groupsBookAllowed: user.groupsBookAllowed,
      usersBookAllowed: user.usersBookAllowed,
    };

    await saveUser(data, companyId, id);
    if (newPhoto) await saveProfileImage(newPhoto.formData);
  };

  const handleSubmit = (values: AdminUser) => {
    if (!isDateOfBirthValid()) {
      return;
    }

    setLoading(true);

    handleSaveUser(values)
      .then(() => {
        toast.add({
          type: 'success',
          message: t('userForm.successToast'),
        });
        history.push('/corporate/travelerProfile');
        setLoading(false);
      })
      .catch((error) => {
        window.scrollTo(0, 0);
        toast.add({
          type: 'danger',
          message: extractErrorMessage(error) || t('defaultError.default'),
        });
        setLoading(false);
      });
  };

  const validationSchema = yup.object().shape({
    name: yup.string().trim().max(100).required(),
    lastName: yup.string().trim().max(100).required(),
    phoneNumber: yup.string().phone().required(),
    email: yup.string().email().required(),
    birthDate_day: yup.number().positive().moreThan(0).lessThan(32, t('guestForm.day.invalidNumber')).required(),
    birthDate_year: yup
      .number()
      .moreThan(1910)
      .lessThan(new Date().getFullYear() + 1, t('guestForm.year.invalidNumber'))
      .required(),
    documentNumber: yup.string().trim().max(100).required(),
    passportNumber: yup.string().trim().max(100),
  });

  const initValues = {
    id: user?.id || '',
    name: user?.name || '',
    lastName: user?.lastName || '',
    phoneNumber: user?.phoneNumber || '',
    email: user?.email || '',
    nationality: user?.nationality || '',
    gender: user?.gender || '',
    birthDate:
      new Date(user?.birthDate || '0001-01-01T00:00:00Z').toISOString() === '0001-01-01T00:00:00Z'
        ? ''
        : formatISODateToDDMMYYYY(user?.birthDate) || '',
    birthDate_day:
      new Date(user?.birthDate || '0001-01-01T00:00:00Z').toISOString() === '0001-01-01T00:00:00Z'
        ? ''
        : moment.utc(user?.birthDate)?.format('DD') || '',
    birthDate_month:
      new Date(user?.birthDate || '0001-01-01T00:00:00Z').toISOString() === '0001-01-01T00:00:00Z'
        ? ''
        : moment.utc(user?.birthDate)?.format('MM') || '',
    birthDate_year:
      new Date(user?.birthDate || '0001-01-01T00:00:00Z').toISOString() === '0001-01-01T00:00:00Z'
        ? ''
        : moment.utc(user?.birthDate)?.format('YYYY') || '',
    documentNumber: user?.documentNumber || '',
    passportNumber: user?.passportNumber || '',
    userGroupId: user?.userGroupId || '',
  };

  const isDateOfBirthValid = () => {
    const birthDate_day = (document.getElementById('input_birthDate_day') as HTMLInputElement)?.value || '';
    const birthDate_year = (document.getElementById('input_birthDate_year') as HTMLInputElement)?.value || '';

    return moment(`${birthDate_year}-${month}-${birthDate_day}`, 'YYYY-MM-DD').isValid();
  };

  const handleChangeMonth = (month: number) => {
    setMonth(month);
  };

  useEffect(() => {
    if (user && user.birthDate !== undefined && user?.birthDate !== '0001-01-01T00:00:00Z') {
      setMonth(parseInt(moment.utc(user?.birthDate).format('MM')));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrapper className="target">
      {isLoading && loadingNationalityCountries ? (
        <Loader fullScreen />
      ) : (
        <>
          <Mobile>
            <Header goBack={() => history.push('/corporate/travelerProfile')}>{t('travelerFormProfile.title')}</Header>
          </Mobile>

          <Desktop>
            <ScreenHeader
              title={t('travelerFormProfile.title')}
              subtitle={t('travelerFormProfile.headerTitle')}
              right={
                <CloseButton onClick={() => history.goBack()}>
                  <CloseIcon />
                </CloseButton>
              }
            />
          </Desktop>

          <Container>
            <ProfilePhotoInfo
              saveProfileImage={handleChangePhoto}
              imageSrc={userPhoto}
              isEdit
              name={`${user?.name} ${user?.lastName}`}
              email={user?.email || ''}
            />

            <Formik
              initialStatus={{ rightCheckIfIsValid: true }}
              initialValues={initValues}
              validationSchema={validationSchema}
              onSubmit={(value) => handleSubmit(value as AdminUser)}
              isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
            >
              {({ dirty, isSubmitting, isValid }: FormikValues) => (
                <>
                  <FormikSubmitErrorEffect />

                  <FormContainer marginBottom={dirty}>
                    <Section title={t('travelerFormProfile.basicInformations')}>
                      <Field name="name" label={t('personalDataForm.name')}>
                        <Input placeholder={t('personalDataForm.namePlaceholder')} />
                      </Field>
                      <Field name="lastName" label={t('personalDataForm.lastName')}>
                        <Input placeholder={t('personalDataForm.lastNamePlaceholder')} />
                      </Field>
                      <Field name="email" labelKey="personalDataForm.email">
                        <Input id="email" type="email" disabled={!!(user && !!user.email)} />
                      </Field>
                      <Field name="phoneNumber" label={t('personalDataForm.phoneNumber')}>
                        <Input type="text" inputMode="tel" placeholder={t('personalDataForm.telNumberPlaceHolder')} />
                      </Field>
                      <Field name="gender" labelKey="personalDataForm.genre">
                        <GenreSelector id="gender" />
                      </Field>
                    </Section>

                    <SectionDateOfBirth>
                      <div></div>
                      <Content>
                        <Field name="birthDate_day" label={t('guestForm.day.label')}>
                          <Input
                            type="text"
                            id="input_birthDate_day"
                            mask="99"
                            inputMode="numeric"
                            placeholder={t('guestForm.day.placeholder')}
                          />
                        </Field>
                        <Field name="birthDate_month">
                          <MonthSelect
                            handleChangeDate={handleChangeMonth}
                            initialValue={month?.toString()}
                            name="input_birthDate_month"
                          />
                        </Field>
                        <Field name="birthDate_year" label={t('guestForm.year.label')}>
                          <Input
                            type="text"
                            id="input_birthDate_year"
                            mask="9999"
                            inputMode="numeric"
                            placeholder={t('guestForm.year.placeholder')}
                          />
                        </Field>
                        {!isDateOfBirthValid() && (
                          <>
                            <ErrorValidation>{t('guestForm.dateOfBirthInvalid')}</ErrorValidation>
                          </>
                        )}
                        <div></div>
                      </Content>
                    </SectionDateOfBirth>
                    <Line />

                    <Section title={t('travelerFormProfile.travelInformations')}>
                      <Field name="documentNumber" labelKey="travelerFormProfile.identificationCardNo">
                        <Input
                          id="documentNumber"
                          placeholder={t('travelerFormProfile.identificationCardNoPlaceholder')}
                          onChange={(e) => {
                                    e.currentTarget.value = e.currentTarget.value.replace(/\s/g, '');
                                  }} />                        
                      </Field>
                      <Field name="passportNumber" labelKey="personalDataForm.documentLabel">
                        <Input 
                          id="passportNumber" 
                          placeholder={t('personalDataForm.passportPlaceholder')} 
                          onChange={(e) => {
                                    e.currentTarget.value = e.currentTarget.value.replace(/\s/g, '');
                                  }} />
                      </Field>
                      <Field name="nationality" labelKey={t('userForm.country')}>
                        <NationalitySelector
                          type="nationality"
                          options={nationalityCountries}
                          title={t('userForm.countrySelect')}
                          enableSearch={true}
                        />
                      </Field>
                    </Section>

                    <SubmitButton dirty={dirty || isNewPhoto} isEdit loading={loading}>
                      {t(`${stringGroup}.confirmButton`)}
                    </SubmitButton>
                  </FormContainer>
                </>
              )}
            </Formik>
          </Container>
        </>
      )}
    </Wrapper>
  );
};

export default TravelerFormProfile;
